import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
	type Mesh,
} from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, TextBlock, Control } from "@babylonjs/gui";
import type { Worker } from "@utah-vr/shared";

interface WorkerVisual {
	root: TransformNode;
	leftLeg: TransformNode;
	rightLeg: TransformNode;
	leftArm: TransformNode;
	rightArm: TransformNode;
	target: Vector3;
	walkPhase: number;
	state: Worker;
	label: Rectangle;
	labelText: TextBlock;
	/** Side aisle this worker patrols (-4 or +4). Locked at spawn so they
	 * never traverse through a rack row. */
	sideX: number;
}

const ROLE_VEST_COLOR: Record<string, Color3> = {
	technician: new Color3(0.95, 0.45, 0.15), // orange safety vest
	operator: new Color3(0.2, 0.45, 0.8), // blue uniform
	logistics: new Color3(0.6, 0.85, 0.3), // hi-vis green
};

// Idle/patrol spots in the AISLES between rack rows. The racks sit at
// z = -1, 2, 5, 8 with depth 1.2 each; aisle centers (clear of overhead
// shelves) are z = 0.5, 3.5, 6.5. Workers stay there so their torsos
// don't clip through the rack shelves at y=0.9.
const IDLE_SPOTS: Vector3[] = [
	new Vector3(-4, 0, 0.5),
	new Vector3(-4, 0, 3.5),
	new Vector3(-4, 0, 6.5),
	new Vector3(4, 0, 0.5),
	new Vector3(4, 0, 3.5),
	new Vector3(4, 0, 6.5),
	new Vector3(0, 0, -3), // near the doorway
];

/** Project an in-rack printer position to the nearest aisle position so a
 * worker walking to repair stands in the aisle, not under the shelf. */
function aisleNear(target: Vector3): Vector3 {
	const aisleZs = [-3, 0.5, 3.5, 6.5];
	let best = aisleZs[0]!;
	let bestDist = Math.abs(target.z - best);
	for (const z of aisleZs) {
		const d = Math.abs(target.z - z);
		if (d < bestDist) { best = z; bestDist = d; }
	}
	// Stay on the side of the warehouse the printer's nearest to.
	const x = target.x < 0 ? -4 : 4;
	return new Vector3(x, 0, best);
}

export interface WorkerVisualsManager {
	upsert(w: Worker): void;
	remove(id: string): void;
	tick(dt: number, printerPositions: Map<string, Vector3>): void;
}

export function createWorkerVisuals(scene: Scene, warehouseRoot: TransformNode): WorkerVisualsManager {
	const adt = AdvancedDynamicTexture.CreateFullscreenUI("worker-labels", true, scene);
	const visuals = new Map<string, WorkerVisual>();

	function build(w: Worker): WorkerVisual {
		const root = new TransformNode(`worker-${w.id}`, scene);
		root.parent = warehouseRoot;
		const start = IDLE_SPOTS[visuals.size % IDLE_SPOTS.length]!;
		root.position.copyFrom(start);

		const vestColor = ROLE_VEST_COLOR[w.role] ?? new Color3(0.7, 0.7, 0.7);
		const skinMat = mat(`worker-skin-${w.id}`, new Color3(0.95, 0.85, 0.75), scene);
		const vestMat = mat(`worker-vest-${w.id}`, vestColor, scene);
		const pantsMat = mat(`worker-pants-${w.id}`, new Color3(0.18, 0.22, 0.32), scene);
		const helmetMat = mat(`worker-helmet-${w.id}`, new Color3(0.95, 0.85, 0.1), scene);

		// Body
		const torso = MeshBuilder.CreateBox(`worker-torso-${w.id}`, { width: 0.45, depth: 0.25, height: 0.7 }, scene);
		torso.position.y = 1.15;
		torso.material = vestMat;
		torso.parent = root;

		const head = MeshBuilder.CreateSphere(`worker-head-${w.id}`, { diameter: 0.32, segments: 8 }, scene);
		head.position.y = 1.7;
		head.material = skinMat;
		head.parent = root;

		// Hard hat
		const helmet = MeshBuilder.CreateSphere(`worker-helmet-${w.id}`, { diameter: 0.36, segments: 8, slice: 0.55 }, scene);
		helmet.position.y = 1.78;
		helmet.material = helmetMat;
		helmet.parent = root;

		const limb = (name: string, isLeg: boolean, sideX: number) => {
			const pivot = new TransformNode(name, scene);
			pivot.parent = root;
			pivot.position.set(sideX, isLeg ? 0.8 : 1.4, 0);
			const m = MeshBuilder.CreateBox(`${name}-mesh`, { width: 0.15, depth: 0.15, height: 0.7 }, scene);
			m.position.y = -0.35;
			m.material = isLeg ? pantsMat : vestMat;
			m.parent = pivot;
			return pivot;
		};
		const leftLeg = limb(`worker-llg-${w.id}`, true, -0.12);
		const rightLeg = limb(`worker-rlg-${w.id}`, true, 0.12);
		const leftArm = limb(`worker-larm-${w.id}`, false, -0.3);
		const rightArm = limb(`worker-rarm-${w.id}`, false, 0.3);

		// 3D name + status label
		const labelAnchor = MeshBuilder.CreateBox(`worker-anchor-${w.id}`, { size: 0.05 }, scene);
		labelAnchor.parent = root;
		labelAnchor.position.y = 2.2;
		labelAnchor.isVisible = false;

		const label = new Rectangle(`worker-label-${w.id}`);
		label.width = "150px";
		label.height = "40px";
		label.cornerRadius = 6;
		label.color = "#fc7";
		label.thickness = 1;
		label.background = "rgba(0,0,0,0.7)";
		adt.addControl(label);
		label.linkWithMesh(labelAnchor as unknown as Mesh);
		label.linkOffsetY = -10;

		const labelText = new TextBlock();
		labelText.text = `${w.displayName}\n${w.role}`;
		labelText.color = "#fff";
		labelText.fontSize = 11;
		labelText.fontWeight = "bold";
		labelText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
		label.addControl(labelText);

		// Lock each worker to one side aisle (-4 or +4) at spawn. Their idle
		// targets only move along Z on that side, so they never cross a rack.
		const sideX = Math.random() < 0.5 ? -4 : 4;
		return {
			root,
			leftLeg,
			rightLeg,
			leftArm,
			rightArm,
			target: start.clone(),
			walkPhase: Math.random() * Math.PI * 2,
			state: w,
			label,
			labelText,
			sideX,
		};
	}

	const upsert = (w: Worker) => {
		const existing = visuals.get(w.id);
		if (existing) {
			existing.state = w;
			return;
		}
		visuals.set(w.id, build(w));
	};

	const remove = (id: string) => {
		const v = visuals.get(id);
		if (!v) return;
		v.label.dispose();
		v.root.dispose();
		visuals.delete(id);
	};

	const tick = (dt: number, printerPositions: Map<string, Vector3>) => {
		const aisleZs = [-3, 0.5, 3.5, 6.5];
		for (const v of visuals.values()) {
			// Pick a target based on current task. Workers stay on their
			// side aisle (sideX = -4 or +4) and only step Z values that are
			// between racks, so they never clip through a rack shelf.
			if (v.state.currentTask === "repairing") {
				const first = printerPositions.values().next().value as Vector3 | undefined;
				if (first) {
					// Project printer's Z to nearest aisle Z, stay on this worker's side.
					let bestZ = aisleZs[0]!;
					let bestDist = Math.abs(first.z - bestZ);
					for (const z of aisleZs) {
						const d = Math.abs(first.z - z);
						if (d < bestDist) { bestZ = z; bestDist = d; }
					}
					v.target = new Vector3(v.sideX, 0, bestZ);
				}
				v.labelText.text = `${v.state.displayName}\nREPAIRING`;
				v.label.color = "#f88";
			} else {
				v.labelText.text = `${v.state.displayName}\n${v.state.role}`;
				v.label.color = "#fc7";
				// Drift among aisle spots on THIS worker's side only.
				if (Vector3.Distance(v.root.position, v.target) < 0.4) {
					const z = aisleZs[Math.floor(Math.random() * aisleZs.length)]!;
					v.target = new Vector3(v.sideX, 0, z);
				}
			}

			const dx = v.target.x - v.root.position.x;
			const dz = v.target.z - v.root.position.z;
			const dist = Math.hypot(dx, dz);
			if (dist > 0.1) {
				const speed = 0.8 * dt;
				v.root.position.x += (dx / dist) * speed;
				v.root.position.z += (dz / dist) * speed;
				v.root.rotation.y = Math.atan2(dx, dz);

				v.walkPhase += dt * 7;
				const swing = Math.sin(v.walkPhase) * 0.5;
				v.leftLeg.rotation.x = swing;
				v.rightLeg.rotation.x = -swing;
				v.leftArm.rotation.x = -swing * 0.7;
				v.rightArm.rotation.x = swing * 0.7;
			}
		}
	};

	return { upsert, remove, tick };
}

function mat(name: string, color: Color3, scene: Scene): StandardMaterial {
	const m = new StandardMaterial(name, scene);
	m.diffuseColor = color;
	return m;
}
